import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Users, Eye, Loader2, ImageOff, ArrowLeft, ArrowRight, Check, Film, Sparkles, Heart, Star, PenLine, Camera, Pencil, Upload, X, Trash2 } from "lucide-react";
import api, { imageUrl } from "@/lib/api";
import StarRating from "@/components/StarRating";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const formatViews = (n) => {
  n = n || 0;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return `${n}`;
};

// A dash of editorial flavour per category (masthead emoji + kicker line).
const PATH_FLAVOR = {
  Food: { emoji: "🍝", kicker: "Recipes, technique & the joy of the table" },
  Fitness: { emoji: "💪", kicker: "Sweat, reps and honest transformations" },
  Health: { emoji: "🌿", kicker: "Feel-good habits and real wellbeing" },
  Tech: { emoji: "⚙️", kicker: "Builds, tips and the tools that matter" },
  "Video Games": { emoji: "🎮", kicker: "Speedruns, secrets and pro strategy" },
  Automotive: { emoji: "🏎️", kicker: "Garage wisdom, restos and road stories" },
  Music: { emoji: "🎵", kicker: "Play it, write it, record it, share it" },
  Travel: { emoji: "✈️", kicker: "Where to go and how to do it right" },
  Fashion: { emoji: "🧵", kicker: "Style, fit and making it your own" },
  Home: { emoji: "🏡", kicker: "Fix it, build it, love where you live" },
  Gardening: { emoji: "🌱", kicker: "Dig in — grow something wonderful" },
  Animals: { emoji: "🐾", kicker: "For the creatures we care for" },
  Sports: { emoji: "🏅", kicker: "Skill, drills and game-day glory" },
  Games: { emoji: "♟️", kicker: "Chess, cards, darts & game-night glory" },
  Finance: { emoji: "💰", kicker: "Money moves, plainly explained" },
  History: { emoji: "📜", kicker: "The stories that shaped us" },
  Biographies: { emoji: "🎩", kicker: "Lives worth learning from" },
  Philosophy: { emoji: "💭", kicker: "Big questions, examined together" },
  "Current News": { emoji: "📰", kicker: "The latest, reported fresh — facts as they break" },
  Hobbies: { emoji: "🎨", kicker: "Whatever lights you up" },
  "Self Help": { emoji: "🌟", kicker: "Small steps to a better you" },
  Collectibles: { emoji: "🏆", kicker: "The hunt, the finds, the bragging rights" },
};
const flavorFor = (c) => PATH_FLAVOR[c] || { emoji: "✨", kicker: "A living community of makers" };

const serif = { fontFamily: "Georgia, 'Times New Roman', serif" };

// Headline fonts — expressive display faces (loaded on demand only on the Path page).
const PATH_FONTS = [
  { key: "magazine", label: "Magazine Serif", css: "'Playfair Display', Georgia, serif" },
  { key: "impact", label: "Bold Impact", css: "'Anton', sans-serif" },
  { key: "condensed", label: "Tall Condensed", css: "'Bebas Neue', sans-serif" },
  { key: "oswald", label: "Condensed Sans", css: "'Oswald', sans-serif" },
  { key: "poppins", label: "Modern Rounded", css: "'Poppins', sans-serif" },
  { key: "script", label: "Handwritten Script", css: "'Pacifico', cursive" },
  { key: "retro", label: "Retro Tech", css: "'Righteous', sans-serif" },
  { key: "future", label: "Futuristic", css: "'Audiowide', sans-serif" },
  { key: "roman", label: "Classic Roman", css: "'Cinzel', serif" },
  { key: "lobster", label: "Retro Script", css: "'Lobster', cursive" },
  { key: "urban", label: "Urban Display", css: "'Bungee', cursive" },
  { key: "slab", label: "Heavy Slab", css: "'Alfa Slab One', serif" },
  { key: "fashion", label: "Fashion Display", css: "'Abril Fatface', serif" },
];
const fontCss = (key) => (PATH_FONTS.find((f) => f.key === key) || PATH_FONTS[0]).css;

const PATH_COLORS = ["#ffffff", "#ffd21e", "#e11d2a", "#1e6fff", "#22c55e", "#ff2d9b", "#ff7a1a", "#8b2fd6", "#d4af37", "#0d9488", "#111111"];

const FONTS_HREF = "https://fonts.googleapis.com/css2?family=Anton&family=Bebas+Neue&family=Oswald:wght@600;700&family=Poppins:wght@700;800&family=Pacifico&family=Righteous&family=Audiowide&family=Cinzel:wght@700;800&family=Lobster&family=Bungee&family=Alfa+Slab+One&family=Abril+Fatface&family=Playfair+Display:wght@700;800;900&display=swap";
function loadPathFonts() {
  if (typeof document === "undefined" || document.getElementById("path-display-fonts")) return;
  const l = document.createElement("link");
  l.id = "path-display-fonts"; l.rel = "stylesheet"; l.href = FONTS_HREF;
  document.head.appendChild(l);
}

function Stat({ value, label, testid }) {
  return (
    <div className="text-center px-3" data-testid={testid}>
      <div className="text-2xl sm:text-3xl font-extrabold text-white leading-none">{value}</div>
      <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.15em] text-[#8fbf82] mt-1.5 font-bold">{label}</div>
    </div>
  );
}

function VideoCard({ m, onOpen, i }) {
  return (
    <div
      data-testid={`path-video-${m.id}`}
      onClick={() => onOpen(m.id)}
      className="overflow-hidden text-left group cursor-pointer rounded-2xl sms-fade-up"
      style={{ background: "#0f0f16", border: "1.5px solid #5d8a52", animationDelay: `${i * 0.03}s` }}
    >
      <div className="w-full aspect-video bg-[#100d1c] overflow-hidden relative">
        {m.hero_image_id ? (
          <img src={imageUrl(m.hero_image_id)} alt={m.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#b7b3c8]"><ImageOff size={26} /></div>
        )}
        {m.has_video && (
          <span className="absolute top-2 left-2 flex items-center gap-1 text-[10px] font-extrabold text-white bg-black/55 px-2 py-0.5 rounded-full">
            <Film size={11} /> VIDEO
          </span>
        )}
        {(m.did_it_count || 0) > 0 && (
          <span data-testid={`path-didit-${m.id}`} className="absolute top-2 right-2 flex items-center gap-1 text-[10px] font-extrabold text-[#0f0f16] bg-[#8fbf82] px-2 py-0.5 rounded-full shadow">
            <Check size={11} strokeWidth={3} /> {m.did_it_count} did it
          </span>
        )}
      </div>
      <div className="p-2.5">
        <div className="font-bold text-[13px] leading-snug line-clamp-2 min-h-[2.4em] text-white">{m.title}</div>
        <div className="flex items-center justify-between gap-2 mt-1.5">
          <StarRating value={m.rating_avg} count={m.rating_count} size={13} />
          <span className="flex items-center gap-1 text-[11px] font-semibold text-white/45 shrink-0">
            <Eye size={12} /> {formatViews(m.views)}
          </span>
          {(m.loves || 0) > 0 && (
            <span className="flex items-center gap-1 text-[11px] font-semibold text-[#e0455c] shrink-0">
              <Heart size={12} fill="#e0455c" /> {m.loves}
            </span>
          )}
        </div>
        {m.creator_username && (
          <div className="mt-1.5 text-[11px] text-[#8fbf82] font-semibold truncate">
            {m.avatar?.emoji}{m.avatar?.tag || m.creator_username}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PathCommunity() {
  const { topic } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [joining, setJoining] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const isAdmin = !!user?.unlimited;
  // Admin settings-card working state (seeded when the lightbox opens)
  const [seHeadline, setSeHeadline] = useState("");
  const [seFont, setSeFont] = useState("magazine");
  const [seColor, setSeColor] = useState("#ffffff");
  const [seCustom, setSeCustom] = useState("#8fbf82");
  const [seOutline, setSeOutline] = useState(false);
  const [seOutlineColor, setSeOutlineColor] = useState("#000000");
  const [seFeatured, setSeFeatured] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [subtopic, setSubtopic] = useState("");

  useEffect(() => { loadPathFonts(); }, []);

  const load = useCallback(async (p, sub) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/paths/${encodeURIComponent(topic)}`, { params: { page: p, subtopic: sub || undefined } });
      setData(data);
      setPage(data.page || 1);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [topic]);

  useEffect(() => { window.scrollTo(0, 0); setSubtopic(""); load(1, ""); }, [load]);

  const changeSubtopic = (val) => {
    setSubtopic(val);
    load(1, val);
  };

  const toggleJoin = async () => {
    if (!user) { toast.error("Sign in to join this Path"); navigate("/login"); return; }
    setJoining(true);
    try {
      const { data: res } = await api.post(`/paths/${encodeURIComponent(topic)}/join`);
      setData((d) => ({ ...d, joined: res.joined, member_count: res.member_count }));
      toast.success(res.joined ? `You're on the ${data.category} Path 🎉` : "Left the Path");
    } catch { toast.error("Could not update — try again"); }
    finally { setJoining(false); }
  };

  const onBannerFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("Image too large (max 12MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => saveBanner(reader.result);
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const saveBanner = async (image_b64) => {
    setSavingBanner(true);
    try {
      const { data: res } = await api.post(`/paths/${encodeURIComponent(topic)}/banner`, { image_b64 });
      setData((d) => ({ ...d, banner_image_id: res.banner_image_id }));
      toast.success(res.banner_image_id ? "Banner updated ✨" : "Banner cleared");
    } catch { toast.error("Could not save banner"); }
    finally { setSavingBanner(false); }
  };

  const openEditor = () => {
    if (!data) return;
    const preset = PATH_COLORS.includes(data.color);
    setSeHeadline(data.headline || "");
    setSeFont(data.font || "magazine");
    setSeColor(data.color ? (preset ? data.color : "custom") : "#ffffff");
    if (data.color && !preset) setSeCustom(data.color);
    setSeOutline(!!data.outline);
    setSeOutlineColor(data.outline_color || "#000000");
    setSeFeatured(data.featured_manual_id || "");
    setEditOpen(true);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = {
        headline: seHeadline.trim(),
        font: seFont,
        color: seColor === "custom" ? seCustom : seColor,
        outline: seOutline,
        outline_color: seOutlineColor,
        featured_manual_id: seFeatured || "",
      };
      await api.post(`/paths/${encodeURIComponent(topic)}/settings`, payload);
      toast.success("Path saved ✨");
      setEditOpen(false);
      await load(page);
    } catch { toast.error("Could not save the Path settings"); }
    finally { setSavingSettings(false); }
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#000" }}>
        <Loader2 className="animate-spin text-white" size={34} />
      </div>
    );
  }
  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6" style={{ background: "#000" }}>
        <div className="rounded-3xl p-8 text-center max-w-md" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }}>
          <div className="text-5xl mb-3">🧭</div>
          <div className="text-white font-extrabold text-xl mb-2">This Path doesn't exist yet</div>
          <p className="text-white/60 text-sm mb-5">Browse the community and pick a category to explore.</p>
          <button onClick={() => navigate("/")} className="font-extrabold text-white px-5 py-2.5 rounded-full" style={{ background: "#4f7746", border: "1.5px solid #8fbf82" }}>
            Back to Community
          </button>
        </div>
      </div>
    );
  }

  const cat = data.category;
  const fl = flavorFor(cat);
  // Featured video = admin pick (from backend), else newest. Dedupe from the grid.
  const feature = data.featured || (data.videos || [])[0] || null;
  // Show the full collection INCLUDING the featured video (it also headlines the masthead above).
  const rest = (data.videos || []);
  // Custom headline styling (admin-set) — text override, font, colour, outline.
  const headlineText = data.headline || cat;
  const oc = data.outline_color || "#000000";
  const headlineStyle = {
    fontFamily: fontCss(data.font || "magazine"),
    color: data.color || "#ffffff",
    textShadow: data.outline ? `-1.5px -1.5px 0 ${oc}, 1.5px -1.5px 0 ${oc}, -1.5px 1.5px 0 ${oc}, 1.5px 1.5px 0 ${oc}, 0 2px 8px rgba(0,0,0,0.45)` : "0 2px 12px rgba(0,0,0,0.35)",
  };

  return (
    <div className="min-h-screen pb-16" style={{ background: "#000" }} data-testid="path-community-page">
      <div className="max-w-6xl mx-auto px-3 sm:px-5 pt-4 sm:pt-6">
        <button onClick={() => navigate("/")} data-testid="path-back-btn"
          className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm font-bold mb-4 transition-colors">
          <ArrowLeft size={16} /> Community
        </button>

        {/* ── Masthead — the magazine cover ── */}
        <div className="rounded-3xl overflow-hidden relative" style={{ background: "#0f0f16", border: "2px solid #5d8a52", boxShadow: "0 0 40px rgba(0,0,0,0.35)" }} data-testid="path-masthead">
          <div className="px-5 sm:px-10 pt-8 pb-7 relative overflow-hidden">
            {/* Custom generated banner (admin-set) sits behind the headline, with a scrim for legibility */}
            {data.banner_image_id && (
              <>
                <img src={`${imageUrl(data.banner_image_id)}?w=1280&q=42`} alt="" data-testid="path-banner-image"
                  className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(15,15,22,0.78) 0%, rgba(15,15,22,0.58) 45%, rgba(15,15,22,0.25) 100%)" }} />
              </>
            )}
            {isAdmin && (
              <button
                data-testid="path-admin-edit-btn"
                onClick={openEditor}
                title="Admin — edit this Path"
                className="absolute top-4 right-4 z-20 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-full hover:scale-105 transition-transform"
                style={{ background: "#16241a", border: "1.5px solid #8fbf82", color: "#8fbf82" }}
              >
                <Pencil size={14} /> Edit
              </button>
            )}
            <div className="relative z-10">
              <div className="flex items-center gap-2 text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#8fbf82] font-bold mb-4">
                <span className="w-8 h-px bg-[#5d8a52]" /> The Vittyyo Path <span className="flex-1 h-px bg-[#5d8a52]/40" />
              </div>
              <div className="min-w-0">
                <h1 className="text-4xl sm:text-6xl font-extrabold leading-[0.95] tracking-tight" style={headlineStyle} data-testid="path-title">{headlineText}</h1>
                <p className="text-[#cbd8c4] text-sm sm:text-lg mt-2 italic" style={serif}>{fl.kicker}</p>
              </div>

              <div className="flex items-center gap-2 sm:gap-4 mt-7 flex-wrap">
                <button
                  data-testid="path-join-btn"
                  onClick={toggleJoin}
                  disabled={joining}
                  className="flex items-center gap-2 font-extrabold px-6 py-3 rounded-full transition-all disabled:opacity-60 hover:scale-[1.03]"
                  style={data.joined
                    ? { background: "transparent", border: "2px solid #8fbf82", color: "#8fbf82" }
                    : { background: "#8fbf82", color: "#0f0f16", boxShadow: "0 0 24px rgba(143,191,130,0.4)" }}
                >
                  {joining ? <Loader2 size={18} className="animate-spin" /> : data.joined ? <Check size={18} /> : <Users size={18} />}
                  {data.joined ? "On the Path" : "Join this Path"}
                </button>
                <div className="text-white/70 text-sm flex items-center gap-3">
                  {data.member_faces?.length > 0 && (
                    <div className="flex items-center -space-x-2" data-testid="path-faces">
                      {data.member_faces.map((f, i) => (
                        <span
                          key={i}
                          title={`${f.emoji || ""}${f.tag || ""}`}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-base leading-none ring-2 ring-[#0f0f16]"
                          style={{ background: "#16161f", border: "1.5px solid #5d8a52" }}
                        >
                          {f.emoji || "🧑"}
                        </span>
                      ))}
                    </div>
                  )}
                  <span>
                    <span className="font-extrabold text-white text-base" data-testid="path-member-count">{data.member_count}</span> {data.member_count === 1 ? "member" : "members"} walking this Path
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stat ribbon */}
          <div className="grid grid-cols-4 divide-x divide-white/10 border-t border-white/10 py-5" style={{ background: "#0a0a0f" }} data-testid="path-stat-ribbon">
            <Stat value={data.video_count} label="Videos" testid="path-stat-videos" />
            <Stat value={data.creator_count} label="Contributors" testid="path-stat-makers" />
            <Stat value={formatViews(data.total_views)} label="Views" testid="path-stat-views" />
            <div className="text-center px-2 flex flex-col items-center justify-center" data-testid="path-stat-featured-creator">
              {data.featured_creator ? (
                <button
                  onClick={() => data.featured_creator.username && navigate(`/creator/${data.featured_creator.username}`)}
                  className="flex flex-col items-center gap-1.5 group"
                  title="Featured Creator — the most-loved maker on this Path"
                  data-testid="path-featured-creator-btn"
                >
                  <span className="text-lg sm:text-2xl font-extrabold text-white leading-none group-hover:text-[#8fbf82] transition-colors whitespace-nowrap">
                    {data.featured_creator.emoji}{data.featured_creator.tag}
                  </span>
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-[#8fbf82] font-bold">Featured Creator</span>
                </button>
              ) : (
                <div className="flex flex-col items-center gap-1.5 opacity-70">
                  <span className="text-lg sm:text-2xl leading-none">🌟</span>
                  <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.12em] text-[#8fbf82] font-bold">Featured Creator</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Feature story ── */}
        {feature && (
          <section className="mt-8" data-testid="path-feature">
            <SectionHead icon={Sparkles} label="The Feature" />
            <div
              onClick={() => navigate(`/community/${feature.id}`)}
              className="grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden cursor-pointer group"
              style={{ background: "#0f0f16", border: "2px solid #5d8a52" }}
            >
              <div className="aspect-video md:aspect-auto bg-[#100d1c] overflow-hidden">
                {feature.hero_image_id ? (
                  <img src={imageUrl(feature.hero_image_id)} alt={feature.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : <div className="w-full h-full flex items-center justify-center text-[#b7b3c8]"><ImageOff size={30} /></div>}
              </div>
              <div className="p-6 sm:p-8 flex flex-col justify-center">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#8fbf82] font-bold mb-3">Now on the {cat} Path</div>
                <h2 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight" style={serif}>{feature.title}</h2>
                <div className="flex items-center gap-4 mt-4">
                  <StarRating value={feature.rating_avg} count={feature.rating_count} size={15} />
                  <span className="flex items-center gap-1.5 text-sm text-white/50"><Eye size={14} /> {formatViews(feature.views)}</span>
                </div>
                {feature.creator_username && (
                  <div className="mt-4 text-sm text-[#8fbf82] font-semibold">{feature.avatar?.emoji}{feature.avatar?.tag || feature.creator_username}</div>
                )}
                <span className="mt-5 inline-flex items-center gap-1.5 text-white font-extrabold group-hover:gap-3 transition-all">
                  Watch it <ArrowRight size={18} />
                </span>
              </div>
            </div>
          </section>
        )}

        {/* ── Makers on this Path ── */}
        {data.top_creators?.length > 0 && (
          <section className="mt-9" data-testid="path-makers">
            <SectionHead icon={PenLine} label="Contributors on this Path" />
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {data.top_creators.map((c, i) => (
                <button
                  key={i}
                  data-testid={`path-maker-${i}`}
                  onClick={() => c.username && navigate(`/creator/${c.username}`)}
                  className="shrink-0 w-[92px] sm:w-[104px] rounded-[1.6rem] px-2 py-4 text-center hover:brightness-110 transition-all"
                  style={{ background: "#0f0f16", border: "1.5px solid #5d8a52" }}
                >
                  <div className="text-3xl mb-2">{c.emoji || "🧑‍🎨"}</div>
                  <div className="font-extrabold text-white text-xs truncate">{c.tag}</div>
                  <div className="text-[10px] text-[#8fbf82] mt-1 font-semibold">{c.videos} video{c.videos === 1 ? "" : "s"}</div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* ── The Collection (grid) ── */}
        {(rest.length > 0 || (data.subtopics?.length > 0)) && (
          <section className="mt-9" data-testid="path-collection">
            <SectionHead
              icon={Film}
              label={subtopic ? `${subtopic} ${cat}` : "The Collection"}
              right={data.subtopics?.length > 0 ? (
                <select
                  data-testid="path-subtopic-select"
                  value={subtopic}
                  onChange={(e) => changeSubtopic(e.target.value)}
                  className="shrink-0 rounded-full px-3 py-1.5 text-xs font-bold text-white cursor-pointer outline-none"
                  style={{ background: "#0f0f16", border: "1.5px solid #5d8a52" }}
                  title="Filter by cuisine"
                >
                  <option value="">All {cat}</option>
                  {data.subtopics.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : null}
            />
            {rest.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                {rest.map((m, i) => <VideoCard key={m.id} m={m} i={i} onOpen={(id) => navigate(`/community/${id}`)} />)}
              </div>
            ) : !feature ? (
              <div className="rounded-2xl p-8 text-center" style={{ background: "#0f0f16", border: "1.5px dashed #5d8a52" }} data-testid="path-collection-empty">
                <div className="text-3xl mb-2">🍽️</div>
                <p className="text-white/70 text-sm">No {subtopic} {cat} videos yet — be the first to add one to this collection!</p>
              </div>
            ) : null}
            {data.pages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-7">
                <button data-testid="path-prev" disabled={page <= 1} onClick={() => load(page - 1, subtopic)}
                  className="px-4 py-2 rounded-full text-white font-bold disabled:opacity-40" style={{ background: "#0f0f16", border: "1.5px solid #5d8a52" }}>Prev</button>
                <span className="text-white/80 text-sm font-bold">Page {page} of {data.pages}</span>
                <button data-testid="path-next" disabled={page >= data.pages} onClick={() => load(page + 1, subtopic)}
                  className="px-4 py-2 rounded-full text-white font-bold disabled:opacity-40" style={{ background: "#0f0f16", border: "1.5px solid #5d8a52" }}>Next</button>
              </div>
            )}
          </section>
        )}

        {/* ── They Did It — the proof wall ── */}
        <section className="mt-9" data-testid="path-proof-wall">
          <SectionHead icon={Camera} label="They Did It" />
          {data.proofs?.length > 0 ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-3 [column-fill:_balance]">
              {data.proofs.map((p) => (
                <div key={p.id} data-testid={`path-proof-${p.id}`}
                  onClick={() => navigate(`/community/${p.manual_id}`)}
                  className="mb-3 break-inside-avoid rounded-2xl overflow-hidden cursor-pointer group"
                  style={{ background: "#0f0f16", border: "1.5px solid #5d8a52" }}>
                  {p.photo_id && (
                    <div className="overflow-hidden">
                      <img src={imageUrl(p.photo_id)} alt="result" loading="lazy"
                        className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-300" />
                    </div>
                  )}
                  <div className="p-3.5">
                    <div className="flex items-center gap-1.5 text-xs">
                      <span className="font-bold text-[#8fbf82]">{p.author_emoji}{p.author_tag}</span>
                      <span className="text-[#8fbf82]/70 flex items-center gap-0.5"><Check size={12} /> did it</span>
                    </div>
                    {p.text && <p className="text-sm text-white/85 mt-1.5 leading-relaxed" style={serif}>“{p.text}”</p>}
                    {p.manual_title && <div className="text-[11px] text-white/40 mt-2 uppercase tracking-wide font-semibold truncate">on “{p.manual_title}”</div>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl p-8 text-center" style={{ background: "#0f0f16", border: "1.5px dashed #5d8a52" }} data-testid="path-proof-empty">
              <div className="text-3xl mb-2">📸</div>
              <p className="text-white/70 text-sm">No results posted on this Path yet. Finish a video and be the first to prove you did it.</p>
            </div>
          )}
        </section>
      </div>

      {/* ── Admin edit lightbox ── */}
      {editOpen && isAdmin && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }} data-testid="path-edit-modal" onClick={() => setEditOpen(false)}>
          <div className="w-full max-w-lg rounded-3xl overflow-hidden max-h-[92vh] flex flex-col" style={{ background: "#0f0f16", border: "2px solid #5d8a52", boxShadow: "0 0 50px rgba(0,0,0,0.5)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
              <div className="flex items-center gap-2 text-white font-extrabold" style={serif}>
                <Pencil size={16} className="text-[#8fbf82]" /> Edit the {cat} Path
              </div>
              <button data-testid="path-edit-close" onClick={() => setEditOpen(false)} className="text-white/50 hover:text-white transition-colors"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              {/* ── Headline ── */}
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#8fbf82] font-bold">Headline</div>
              <input
                data-testid="path-headline-input" value={seHeadline} maxLength={60}
                onChange={(e) => setSeHeadline(e.target.value)} placeholder={cat}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none" style={{ background: "#14141b", border: "1.5px solid #2a3a26" }}
              />
              {/* Live preview */}
              <div className="rounded-2xl p-4 flex items-center justify-center min-h-[76px]" style={{ background: "#0a0a0f", border: "1.5px solid #2a3a26" }} data-testid="path-headline-preview">
                <span className="text-3xl sm:text-4xl font-extrabold leading-none text-center" style={{
                  fontFamily: fontCss(seFont),
                  color: seColor === "custom" ? seCustom : seColor,
                  textShadow: seOutline ? `-1.5px -1.5px 0 ${seOutlineColor}, 1.5px -1.5px 0 ${seOutlineColor}, -1.5px 1.5px 0 ${seOutlineColor}, 1.5px 1.5px 0 ${seOutlineColor}` : "none",
                }}>{seHeadline.trim() || cat}</span>
              </div>

              {/* Font */}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.18em] text-[#8fbf82] font-bold mb-1.5">Font</label>
                <select data-testid="path-font-select" value={seFont} onChange={(e) => setSeFont(e.target.value)}
                  className="w-full rounded-xl px-3 py-2.5 text-sm font-bold text-white cursor-pointer outline-none" style={{ background: "#14141b", border: "1.5px solid #2a3a26" }}>
                  {PATH_FONTS.map((f) => <option key={f.key} value={f.key} style={{ fontFamily: f.css }}>{f.label}</option>)}
                </select>
              </div>

              {/* Color */}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.18em] text-[#8fbf82] font-bold mb-1.5">Colour</label>
                <div className="flex flex-wrap gap-2 items-center" data-testid="path-color-row">
                  {PATH_COLORS.map((c) => (
                    <button key={c} data-testid={`path-color-${c.replace('#','')}`} onClick={() => setSeColor(c)}
                      className="w-8 h-8 rounded-full relative transition-transform hover:scale-110"
                      style={{ background: c, border: seColor === c ? "3px solid #8fbf82" : "2px solid rgba(255,255,255,0.2)" }}>
                      {seColor === c && <Check size={14} className="absolute inset-0 m-auto" style={{ color: (c === "#ffffff" || c === "#ffd21e" || c === "#d4af37") ? "#111" : "#fff" }} />}
                    </button>
                  ))}
                  <button data-testid="path-color-custom" title="Custom colour" className="w-8 h-8 rounded-full relative flex items-center justify-center overflow-hidden"
                    style={{ background: seCustom, border: seColor === "custom" ? "3px solid #8fbf82" : "2px solid rgba(255,255,255,0.2)" }}>
                    <Pencil size={12} style={{ color: "#fff", mixBlendMode: "difference" }} />
                    <input type="color" value={seCustom} onChange={(e) => { setSeCustom(e.target.value); setSeColor("custom"); }} className="absolute inset-0 opacity-0 cursor-pointer" />
                  </button>
                </div>
              </div>

              {/* Outline */}
              <div className="rounded-2xl border-2 px-3 py-2.5" style={{ borderColor: "#5d8a52", background: seOutline ? "#12241a" : "#0f0f16" }}>
                <label className="flex items-center gap-2 cursor-pointer" data-testid="path-outline-toggle">
                  <input type="checkbox" checked={seOutline} onChange={(e) => setSeOutline(e.target.checked)} className="w-4 h-4 accent-[#4f7746]" />
                  <span className="font-bold text-white flex items-center gap-1.5 text-sm"><Pencil size={13} className="text-[#8fbf82]" /> Text outline</span>
                </label>
                {seOutline && (
                  <div className="flex flex-wrap gap-2 items-center mt-3" data-testid="path-outline-color-row">
                    {["#000000", "#ffffff", "#1e3aaa", "#e11d2a", "#ffd21e"].map((c) => (
                      <button key={c} onClick={() => setSeOutlineColor(c)} className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                        style={{ background: c, border: seOutlineColor === c ? "3px solid #8fbf82" : "2px solid rgba(255,255,255,0.2)" }} />
                    ))}
                    <label className="w-7 h-7 rounded-full relative overflow-hidden cursor-pointer flex items-center justify-center" title="Custom outline"
                      style={{ background: seOutlineColor, border: "2px solid rgba(255,255,255,0.2)" }}>
                      <Pencil size={11} style={{ color: "#fff", mixBlendMode: "difference" }} />
                      <input type="color" value={seOutlineColor} onChange={(e) => setSeOutlineColor(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" />
                    </label>
                  </div>
                )}
              </div>

              {/* Featured pick */}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.18em] text-[#8fbf82] font-bold mb-1.5">Featured video</label>
                <select data-testid="path-featured-select" value={seFeatured} onChange={(e) => setSeFeatured(e.target.value)}
                  className="w-full rounded-xl px-3 py-2.5 text-sm font-bold text-white cursor-pointer outline-none" style={{ background: "#14141b", border: "1.5px solid #2a3a26" }}>
                  <option value="">Newest video (auto)</option>
                  {(data.videos || []).map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
                </select>
                <p className="text-[11px] text-white/35 mt-1.5">Pick which video headlines "The Feature". Choices are from this page of the Path.</p>
              </div>

              {/* Save */}
              <button data-testid="path-settings-save" onClick={saveSettings} disabled={savingSettings}
                className="w-full inline-flex items-center justify-center gap-2 font-extrabold py-3 rounded-full transition-transform hover:scale-[1.02] disabled:opacity-60" style={{ background: "#8fbf82", color: "#0f0f16" }}>
                {savingSettings ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />} {savingSettings ? "Saving…" : "Save Path"}
              </button>

              <div className="pt-2 border-t border-white/10">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#8fbf82] font-bold mb-2 mt-3">Masthead banner image</div>
                <p className="text-xs text-white/55 leading-relaxed">
                  Upload a custom generated image to make this Path stunning. It fills the headline banner (title + Join button sit on top with a dark scrim).
                  <br />
                  <span className="text-white/80 font-semibold">Recommended size: 1600 × 400 px</span> (a wide 4:1 banner). The live banner renders at roughly <span className="text-white/80 font-semibold">1112 × 275 px</span> on desktop and cover-fits your image.
                </p>

                <div className="rounded-2xl overflow-hidden aspect-[1112/275] bg-[#0a0a0f] flex items-center justify-center mt-3" style={{ border: "1.5px solid #2a3a26" }} data-testid="path-edit-preview">
                  {data.banner_image_id ? (
                    <img src={imageUrl(data.banner_image_id)} alt="banner" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-white/35 text-sm flex flex-col items-center gap-1"><ImageOff size={22} /> No custom banner yet</div>
                  )}
                </div>

                <div className="flex items-center gap-2.5 mt-3">
                  <label data-testid="path-banner-upload" className="flex-1 inline-flex items-center justify-center gap-2 font-extrabold py-3 rounded-full cursor-pointer transition-transform hover:scale-[1.02]" style={{ background: "#1e3aaa", color: "#fff" }}>
                    {savingBanner ? <Loader2 size={17} className="animate-spin" /> : <Upload size={17} />}
                    {savingBanner ? "Uploading…" : (data.banner_image_id ? "Replace image" : "Upload image")}
                    <input type="file" accept="image/*" className="hidden" onChange={onBannerFile} disabled={savingBanner} />
                  </label>
                  {data.banner_image_id && (
                    <button data-testid="path-banner-remove" onClick={() => saveBanner("")} disabled={savingBanner}
                      className="inline-flex items-center gap-1.5 font-bold px-4 py-3 rounded-full disabled:opacity-50" style={{ background: "transparent", border: "1.5px solid #e0455c", color: "#e0455c" }}>
                      <Trash2 size={16} /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHead({ icon: Icon, label, right }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0f0f16", border: "1.5px solid #5d8a52" }}>
        <Icon size={17} className="text-[#8fbf82]" />
      </span>
      <h3 className="text-white font-extrabold text-lg uppercase tracking-[0.12em]" style={serif}>{label}</h3>
      <span className="flex-1 h-px bg-white/15" />
      {right}
    </div>
  );
}
